import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DollarSign, Calendar, User, AlertTriangle, CheckCircle, 
  Clock, Plus, Edit, Trash2, X, Download, FileText,
  ChevronLeft, ChevronRight, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const PayrollManager = ({ isAdmin = false }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: null,
    status: null,
    staff_id: null
  });
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editPayroll, setEditPayroll] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    staff_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    base_salary: 2500,
    overtime_hours: 0,
    overtime_rate: 20,
    bonus: 0,
    deductions: 0,
    due_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchPayrolls();
    if (isAdmin) {
      fetchStaff();
      fetchPendingSummary();
    }
  }, [filters, isAdmin]);

  const fetchPayrolls = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.status) params.append('status', filters.status);
      if (filters.staff_id) params.append('staff_id', filters.staff_id);
      
      const response = await axios.get(`${API}/hr/payroll?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayrolls(response.data.payrolls || []);
    } catch (error) {
      toast.error('Errore caricamento cedolini');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      // Usa /hr/reports/staff-list per lista staff HR
      const response = await axios.get(`${API}/hr/reports/staff-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.staff || []);
    } catch (error) {
      // Fallback: usa /doctors se non funziona
      try {
        const response = await axios.get(`${API}/doctors`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setStaff(response.data.doctors || []);
      } catch (e) {
        console.error('Error fetching staff:', e);
      }
    }
  };

  const fetchPendingSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr/payroll/summary/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleCreatePayroll = async () => {
    if (!formData.staff_id || !formData.due_date) {
      toast.error('Seleziona staff e data scadenza');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/payroll`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cedolino creato');
      setShowCreateModal(false);
      fetchPayrolls();
      fetchPendingSummary();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione cedolino');
    } finally {
      setCreating(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!formData.due_date) {
      toast.error('Inserisci data scadenza');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/hr/payroll/bulk-create?month=${formData.month}&year=${formData.year}&base_salary=${formData.base_salary}&due_date=${formData.due_date}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowBulkModal(false);
      fetchPayrolls();
      fetchPendingSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione bulk');
    } finally {
      setCreating(false);
    }
  };

  const handleMarkAsPaid = async (payroll) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/hr/payroll/${payroll.id}`, {
        status: 'paid',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bonifico'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cedolino marcato come pagato');
      fetchPayrolls();
      fetchPendingSummary();
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  const handleDelete = async (payrollId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cedolino?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr/payroll/${payrollId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cedolino eliminato');
      fetchPayrolls();
      fetchPendingSummary();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      base_salary: 2500,
      overtime_hours: 0,
      overtime_rate: 20,
      bonus: 0,
      deductions: 0,
      due_date: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Pagato</span>;
      case 'overdue':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Scaduto</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> In Attesa</span>;
    }
  };

  const calculateTotal = () => {
    const overtime = formData.overtime_hours * formData.overtime_rate;
    const gross = formData.base_salary + overtime + formData.bonus;
    const net = gross - formData.deductions;
    return { overtime, gross, net };
  };

  return (
    <div className="space-y-6" data-testid="payroll-manager">
      {/* Header con Summary Cards (solo Admin) */}
      {isAdmin && pendingSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Totale da Pagare</p>
                <p className="text-2xl font-bold text-gray-800">€{pendingSummary.total_pending_amount?.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{pendingSummary.total_pending_count} cedolini</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Scaduti</p>
                <p className="text-2xl font-bold text-red-600">€{pendingSummary.overdue_amount?.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-red-400 mt-2">{pendingSummary.overdue_count} cedolini</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">In Scadenza (7gg)</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingSummary.due_soon_count}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center justify-center">
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2 text-sm"
                data-testid="create-payroll-btn"
              >
                <Plus className="w-4 h-4" /> Nuovo
              </button>
              <button
                onClick={() => setShowBulkModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                data-testid="bulk-create-btn"
              >
                <FileText className="w-4 h-4" /> Genera Tutti
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtri */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">Filtri:</span>
          </div>
          
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            {[2024, 2025, 2026].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          
          <select
            value={filters.month || ''}
            onChange={(e) => setFilters({ ...filters, month: e.target.value ? parseInt(e.target.value) : null })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Tutti i mesi</option>
            {MONTH_NAMES.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || null })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Tutti gli stati</option>
            <option value="pending">In Attesa</option>
            <option value="paid">Pagato</option>
            <option value="overdue">Scaduto</option>
          </select>
          
          {isAdmin && (
            <select
              value={filters.staff_id || ''}
              onChange={(e) => setFilters({ ...filters, staff_id: e.target.value || null })}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">Tutto lo staff</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
              ))}
            </select>
          )}
          
          <button
            onClick={fetchPayrolls}
            className="p-2 text-gray-500 hover:text-sky-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Lista Cedolini */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : payrolls.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nessun cedolino trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periodo</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Straordinari</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bonus</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Netto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {payrolls.map((payroll) => (
                  <tr key={payroll.id} className="hover:bg-gray-50" data-testid={`payroll-row-${payroll.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-medium">{MONTH_NAMES[payroll.month]} {payroll.year}</span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{payroll.staff_name}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">€{payroll.base_salary?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {payroll.overtime_hours > 0 ? (
                        <span className="text-blue-600">+€{payroll.overtime_pay?.toLocaleString()}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {payroll.bonus > 0 ? (
                        <span className="text-green-600">+€{payroll.bonus?.toLocaleString()}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 font-bold text-sky-700">€{payroll.net_total?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{payroll.due_date}</td>
                    <td className="px-4 py-3">{getStatusBadge(payroll.status)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {payroll.status !== 'paid' && (
                            <button
                              onClick={() => handleMarkAsPaid(payroll)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Segna come pagato"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(payroll.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Crea Cedolino */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">Nuovo Cedolino</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Staff *</label>
                <select
                  value={formData.staff_id}
                  onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="payroll-staff-select"
                >
                  <option value="">Seleziona staff...</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.role})</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Mese *</label>
                  <select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {MONTH_NAMES.slice(1).map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Anno *</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Stipendio Base (€) *</label>
                <input
                  type="number"
                  value={formData.base_salary}
                  onChange={(e) => setFormData({ ...formData, base_salary: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="payroll-base-salary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Ore Straordinario</label>
                  <input
                    type="number"
                    value={formData.overtime_hours}
                    onChange={(e) => setFormData({ ...formData, overtime_hours: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tariffa Oraria (€)</label>
                  <input
                    type="number"
                    value={formData.overtime_rate}
                    onChange={(e) => setFormData({ ...formData, overtime_rate: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Bonus (€)</label>
                  <input
                    type="number"
                    value={formData.bonus}
                    onChange={(e) => setFormData({ ...formData, bonus: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Trattenute (€)</label>
                  <input
                    type="number"
                    value={formData.deductions}
                    onChange={(e) => setFormData({ ...formData, deductions: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Data Scadenza Pagamento *</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="payroll-due-date"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
              
              {/* Riepilogo */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Riepilogo</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Stipendio Base:</span>
                    <span>€{formData.base_salary?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Straordinari ({formData.overtime_hours}h × €{formData.overtime_rate}):</span>
                    <span>€{calculateTotal().overtime?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bonus:</span>
                    <span>€{formData.bonus?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Trattenute:</span>
                    <span>-€{formData.deductions?.toLocaleString()}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Netto:</span>
                    <span className="text-sky-600">€{calculateTotal().net?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleCreatePayroll}
                disabled={creating}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                data-testid="confirm-create-payroll"
              >
                {creating ? 'Creazione...' : 'Crea Cedolino'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crea Bulk */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">Genera Cedolini per Tutto lo Staff</h3>
              <button onClick={() => setShowBulkModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Mese</label>
                  <select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {MONTH_NAMES.slice(1).map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Anno</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Stipendio Base Standard (€)</label>
                <input
                  type="number"
                  value={formData.base_salary}
                  onChange={(e) => setFormData({ ...formData, base_salary: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Data Scadenza Pagamento *</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p>Verranno generati cedolini per tutti i membri dello staff attivo. I cedolini già esistenti per lo stesso periodo saranno saltati.</p>
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleBulkCreate}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Generazione...' : 'Genera Cedolini'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManager;
